import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useServices } from '@/context/ServiceContext'
import { EpisodeCard } from '@/components/EpisodeCard'
import { RoleBadge } from '@/components/RoleBadge'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { AskSarahButton } from '@/components/sarah/AskSarahButton'
import { LevelBadge } from '@/components/gamification/LevelBadge'
import { StreakIndicator } from '@/components/gamification/StreakIndicator'
import type { Episode } from '@/domain/episode'
import type { EpisodeProgress } from '@/domain/progress'
import type { Organization } from '@/domain/organization'
import type { QuizResult } from '@/domain/result'
import type { BadgeAward, BadgeDefinition } from '@/domain/badge'
import type { Certificate } from '@/domain/certificate'
import type { StreakRecord } from '@/domain/streak'

export function DashboardPage() {
  const { currentUser } = useAuth()
  const { episodes, progress, organizations, results, xp, badges, certificateRepo, streaks } = useServices()

  const [allEpisodes, setAllEpisodes] = useState<Episode[]>([])
  const [myProgress, setMyProgress] = useState<EpisodeProgress[]>([])
  const [org, setOrg] = useState<Organization | null>(null)
  const [myResults, setMyResults] = useState<QuizResult[]>([])
  const [xpTotal, setXpTotal] = useState(0)
  const [myBadgeAwards, setMyBadgeAwards] = useState<BadgeAward[]>([])
  const [badgeDefs, setBadgeDefs] = useState<BadgeDefinition[]>([])
  const [certificates, setCertificates] = useState<Certificate[]>([])
  const [streak, setStreak] = useState<StreakRecord | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!currentUser) return
    Promise.all([
      episodes.list(),
      progress.getForUser(currentUser.id),
      currentUser.orgId ? organizations.getById(currentUser.orgId) : Promise.resolve(null),
      results.listForUser(currentUser.id),
      xp.getTotalForUser(currentUser.id),
      badges.listAwardsForUser(currentUser.id),
      badges.listDefinitions(),
      certificateRepo.listForUser(currentUser.id),
      streaks.getForUser(currentUser.id),
    ]).then(([eps, prog, orgResult, res, xpSum, awards, defs, certs, streakRecord]) => {
      setAllEpisodes(eps)
      setMyProgress(prog)
      setOrg(orgResult)
      setMyResults(res)
      setXpTotal(xpSum)
      setMyBadgeAwards(awards)
      setBadgeDefs(defs)
      setCertificates(certs)
      setStreak(streakRecord)
      setLoading(false)
    })
  }, [currentUser, episodes, progress, organizations, results, xp, badges, certificateRepo, streaks])

  if (!currentUser) return null
  if (loading) return <LoadingSpinner label="Loading your dashboard…" />

  const progressByEpisode = new Map(myProgress.map((p) => [p.episodeId, p]))
  const passedEpisodeIds = new Set(myResults.filter((r) => r.passed).map((r) => r.episodeId))
  const completedCount = myProgress.filter((p) => p.completed).length
  const inProgress = allEpisodes.find((e) => {
    const p = progressByEpisode.get(e.id)
    return p && !p.completed
  })
  const continueEpisode = inProgress ?? allEpisodes.find((e) => !progressByEpisode.has(e.id))

  const earnedBadgeDefs = badgeDefs.filter((d) => myBadgeAwards.some((a) => a.badgeKey === d.badgeKey))
  const validCertificate = certificates.find((c) => c.status === 'valid')

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm text-white/50">Welcome back,</p>
          <h1 className="font-display text-3xl font-bold text-white">{currentUser.displayName.split(' (')[0]}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <RoleBadge role={currentUser.role} />
            {org && <span className="cyber-pill bg-white/5 text-white/60 ring-1 ring-white/10">{org.name}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="cyber-panel px-5 py-3 text-center">
            <p className="text-2xl font-display font-bold text-teal-300">{allEpisodes.length}</p>
            <p className="text-xs text-white/50">Episodes available</p>
          </div>
          <div className="cyber-panel px-5 py-3 text-center">
            <p className="text-2xl font-display font-bold text-amber-300">{completedCount}</p>
            <p className="text-xs text-white/50">Watched</p>
          </div>
          <div className="cyber-panel px-5 py-3 text-center">
            <p className="text-2xl font-display font-bold gradient-text">{xpTotal}</p>
            <p className="text-xs text-white/50">Cyber Points</p>
          </div>
        </div>
      </div>

      <section className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <LevelBadge xpTotal={xpTotal} />
        </div>
        <StreakIndicator streak={streak} />
      </section>

      <section className="mt-6 max-w-sm">
        <Link
          to="/missions"
          className="cyber-panel flex items-center justify-between p-4 transition hover:border-indigo-400/40"
        >
          <div>
            <p className="font-display text-sm font-semibold text-white">Interactive Missions</p>
            <p className="text-xs text-white/50">Stories &amp; the Cybersecurity Escape Room</p>
          </div>
          <span className="text-indigo-300">→</span>
        </Link>
      </section>

      {continueEpisode && (
        <section className="mt-10">
          <h2 className="font-display text-lg font-semibold text-white">
            {progressByEpisode.has(continueEpisode.id) ? 'Continue Learning' : 'Start Learning'}
          </h2>
          <div className="mt-4 max-w-sm">
            <EpisodeCard
              episode={continueEpisode}
              passed={passedEpisodeIds.has(continueEpisode.id)}
              progressPercent={
                progressByEpisode.get(continueEpisode.id)
                  ? Math.round(
                      (progressByEpisode.get(continueEpisode.id)!.positionSeconds /
                        continueEpisode.durationSeconds) *
                        100
                    )
                  : 0
              }
            />
          </div>
        </section>
      )}

      <section className="mt-12">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-white">Episode Library</h2>
          <Link to="/episodes" className="text-sm font-medium text-teal-300 hover:text-teal-200">
            View all →
          </Link>
        </div>
        <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {allEpisodes.slice(0, 3).map((ep) => {
            const p = progressByEpisode.get(ep.id)
            const pct = p ? Math.round((p.positionSeconds / ep.durationSeconds) * 100) : undefined
            return <EpisodeCard key={ep.id} episode={ep} progressPercent={pct} passed={passedEpisodeIds.has(ep.id)} />
          })}
        </div>
      </section>

      <section className="mt-12 grid gap-6 lg:grid-cols-3">
        <div className="cyber-panel p-6 lg:col-span-2">
          <h3 className="font-display font-semibold text-white">Achievements earned</h3>
          {earnedBadgeDefs.length === 0 ? (
            <p className="mt-2 text-sm text-white/60">
              Pass an episode quiz to earn your first badge — check the Episode Library for anything
              marked "Watched."
            </p>
          ) : (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {earnedBadgeDefs.map((b) => (
                <div key={b.badgeKey} className="flex items-center gap-3 rounded-xl bg-white/5 p-3">
                  <span className="text-2xl">{b.iconEmoji}</span>
                  <div>
                    <p className="text-sm font-semibold text-white">{b.name}</p>
                    <p className="text-xs text-white/50">{b.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="cyber-panel flex flex-col justify-between p-6">
          <div>
            <h3 className="font-display font-semibold text-white">Certificate</h3>
            {validCertificate ? (
              <p className="mt-2 text-sm text-white/60">
                You've earned the Foundations Track certificate.
              </p>
            ) : (
              <p className="mt-2 text-sm text-white/60">
                Pass every episode quiz to become eligible for your CyberTales Learning certificate.
              </p>
            )}
          </div>
          {validCertificate && (
            <Link to={`/certificates/${validCertificate.id}`} className="cyber-btn-primary mt-4 justify-center">
              View Certificate
            </Link>
          )}
        </div>
      </section>

      <section className="mt-8 max-w-md">
        {(() => {
          const weakestResult = myResults.reduce<QuizResult | null>((worst, r) => {
            if (!worst || r.scorePercent < worst.scorePercent) return r
            return worst
          }, null)
          if (weakestResult && weakestResult.scorePercent < 80) {
            const weakEpisode = allEpisodes.find((e) => e.id === weakestResult.episodeId)
            return (
              <AskSarahButton
                variant="card"
                label={`You scored ${weakestResult.scorePercent}% on "${weakEpisode?.title ?? 'a recent quiz'}" — want to practice with Sarah?`}
                episodeId={weakestResult.episodeId}
                resultId={weakestResult.id}
                initialPrompt="Help me understand my mistake"
              />
            )
          }
          return <AskSarahButton variant="card" label="Practice with Sarah" />
        })()}
      </section>
    </div>
  )
}
